import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  take,
  concatMap,
  catchError,
  distinctUntilChanged,
  finalize,
  from,
  map,
  of,
  tap,
  toArray,
  switchMap,
  throwError,
} from 'rxjs';
import {
  IAssessment,
  IAssessmentProperties,
  IOverallScore,
  IStartAssessment,
  ISubmitAssessment,
  ISubmitAttachments,
  ISyncQuestion,
  ISyncMainTab,
  DeleteAttachmentPayload,
} from '@core/models/assessment.model';
import { HttpClient } from '@angular/common/http';
import { DBStoreNames, LocalStorageKeys } from '../../configs/app.config';
import {
  ConnectionService,
  IndexedDbService,
  LocalStoreService,
} from '../services/index';

interface AttachmentData {
  attachmentID: number | null;
  filePath: string | null;
  fileData: string | null;
  fileType: string | null;
  fileName: string | null;
  attachmentNotes: string;
  attachmentDeleted?: boolean;
}

interface AttachmentSyncQueueItem {
  questionId: number;
  assessmentProperties: IAssessmentProperties;
  deleteIds: number[];
  uploads: AttachmentData[];
  onSuccess?: (results?: any[]) => void;
  onError?: () => void;
}

interface AttachmentSyncCompletedEvent {
  questionId: number;
  results: any[];
}

@Injectable({
  providedIn: 'root',
})
export class AssessmentService {
  private readonly changesMade = new BehaviorSubject<boolean>(false);
  private readonly assessmentProperties =
    new BehaviorSubject<IAssessmentProperties>({} as IAssessmentProperties);
  private readonly fieldValidations = new Map<number, boolean>();
  private readonly formValid = new BehaviorSubject<boolean>(true);
  private readonly pendingAttachmentSyncQuestions = new Set<number>();
  private readonly attachmentSyncPending = new BehaviorSubject<boolean>(false);
  private readonly attachmentSavedAt = new Subject<Date>();
  private readonly attachmentSyncCompleted =
    new Subject<AttachmentSyncCompletedEvent>();
  private retryNotificationShown = false;

  changesMade$: Observable<boolean> = this.changesMade.asObservable();
  assessmentProperties$: Observable<IAssessmentProperties> =
    this.assessmentProperties.asObservable();
  formValid$: Observable<boolean> = this.formValid.asObservable();
  attachmentSyncPending$: Observable<boolean> =
    this.attachmentSyncPending.asObservable();
  attachmentSavedAt$: Observable<Date> = this.attachmentSavedAt.asObservable();
  attachmentSyncCompleted$: Observable<AttachmentSyncCompletedEvent> =
    this.attachmentSyncCompleted.asObservable();

  private readonly attachmentSyncQueue$ =
    new Subject<AttachmentSyncQueueItem>();
  private readonly pendingQueue: AttachmentSyncQueueItem[] = [];
  private connectionOnline = navigator.onLine;
  private isRetrying = false;
  private readonly inFlightUploadKeys = new Set<string>();

  private readonly syncingQuestions = new Set<number>();
  constructor(
    private readonly localService: LocalStoreService,
    private readonly httpClient: HttpClient,
    private readonly dbService: IndexedDbService,
    private readonly connectionService: ConnectionService,
  ) {
    this.initializeAttachmentSyncQueue();
    this.loadPendingAttachmentSyncQueue();
    this.subscribeToConnectionChanges();
  }

  /**
   * function to set view mode
   *
   * @param {boolean} value
   * @memberof AssessmentService
   */
  setChangesMadeStatus(value: boolean) {
    this.changesMade.next(value);
  }

  /**
   * function to update assessment properties
   *
   * @param {IAssessmentProperties} properties
   * @memberof AssessmentService
   */
  updateAssessmentProperties(properties: IAssessmentProperties): void {
    const updatedProperties = {
      ...this.localService.get(LocalStorageKeys.ASSESSMENT_PROPERTIES),
      ...properties,
    };
    this.localService.store(
      LocalStorageKeys.ASSESSMENT_PROPERTIES,
      updatedProperties,
    );
  }

  /**
   * function to start assessment locally
   *
   * @memberof AssessmentService
   */
  startAssessmentLocal() {
    this.localService.store(LocalStorageKeys.ASSESSMENT_STATUS, true);
    this.localService.store(LocalStorageKeys.ViewOnlyMode, false);
  }

  /**
   * function to end locally started assessment
   *
   * @memberof AssessmentService
   */
  endAssessmentLocal() {
    this.localService.store(LocalStorageKeys.ASSESSMENT_STATUS, false);
    this.localService.store(LocalStorageKeys.ViewOnlyMode, true);
  }

  /**
   * function to return assessment status
   *
   * @return {*}
   * @memberof AssessmentService
   */
  getAssessmentStatus() {
    return this.localService.get(LocalStorageKeys.ASSESSMENT_STATUS);
  }

  /**
   * calls API with assessment ID to get questions to continue assessment
   *
   * @param {number} assessmentID
   * @return {*}  {Observable<IAssessment>}
   * @memberof AssessmentService
   */

  /* istanbul ignore next */
  continueAssessment(assessmentID: number): Observable<IAssessment> {
    return this.httpClient.get<IAssessment>(
      `continue-assessment?AssessmentID=${assessmentID}`,
    );
  }

  /**
   * calls API with assessment ID to get questions to continue assessment
   *
   * @param {IStartAssessment} startAssessmentModel
   * @return {*}  {Observable<IAssessment>}
   * @memberof AssessmentService
   */

  /* istanbul ignore next */
  startAssessment(
    startAssessmentModel: IStartAssessment,
  ): Observable<IAssessment> {
    return this.httpClient.post<IAssessment>(
      `start-assessment`,
      startAssessmentModel,
    );
  }

  /**
   * call backend to get overall score of an assessment
   *
   * @param {number} assessmentID
   * @return {*}  {Observable<IOverallScore>}
   * @memberof AssessmentService
   */

  /* istanbul ignore next */
  getOverallScore(assessmentID: number): Observable<IOverallScore> {
    return this.httpClient.get<IOverallScore>(
      `completed-section-scores?AssessmentID=${assessmentID}`,
    );
  }

  /**
   * upload chunks of images to backend
   *
   * @param {ISubmitAttachments} chunk
   * @return {*}  {Observable<string>}
   * @memberof AssessmentService
   */
  /* istanbul ignore next */
  uploadChunks(chunk: ISubmitAttachments): Observable<string> {
    return this.httpClient.post<string>('/upload-attachments', chunk);
  }

  /**
   * submit and sync assessment data to system
   *
   * @param {ISubmitAssessment} assessmentData
   * @return {*}  {Observable<string>}
   * @memberof AssessmentService
   */
  /* istanbul ignore next */
  syncAssessmentData(assessmentData: ISubmitAssessment): Observable<string> {
    return this.httpClient.post<string>(
      '/sync-assessment-data',
      assessmentData,
    );
  }

  /**
   * clear assessment
   *
   * @param {number} assessmentID
   * @return {*}  {Observable<boolean>}
   * @memberof AssessmentService
   */
  /* istanbul ignore next */
  clearAssessment(assessmentID: number): Observable<boolean> {
    return this.httpClient.post<boolean>('/clear-assessment-data', {
      assessmentID,
    });
  }

  /*/**
   * Sync a main tab responses
   *
   * @param {ISyncMainTab} tabSync
   * @return {*}  {Observable<string>}
   * @memberof AssessmentService
   */
  /* istanbul ignore next */
  saveDraft(tabSync: ISyncMainTab, config: any = {}): Observable<string> {
    return this.httpClient.post<string>('/save-all-draft-answers', tabSync, {
      params: {
        _config: JSON.stringify(config),
      },
    });
  }

  /**
   * loop and clear all indexed db stores
   *
   * @memberof AssessmentService
   */
  clearDBStores(): void {
    const dbStoreNames = Object.values(DBStoreNames);
    dbStoreNames.forEach((storeName) => {
      this.dbService
        .clearStore(storeName)
        .pipe(take(1))
        .subscribe(() => {});
    });
  }

  private subscribeToConnectionChanges(): void {
    this.connectionService
      .getConnectionStatus()
      .pipe(distinctUntilChanged())
      .subscribe((isOnline) => {
        this.connectionOnline = isOnline;

        if (!isOnline) {
          this.isRetrying = false;
          return;
        }

        if (this.pendingQueue.length > 0 && !this.isRetrying) {
          this.isRetrying = true;
          this.retryPendingAttachmentSyncs();
        }
      });
  }

  private loadPendingAttachmentSyncQueue(): void {
    this.dbService
      .getAll<AttachmentSyncQueueItem>(DBStoreNames.ATTACHMENT_SYNC_QUEUE)
      .pipe(take(1))
      .subscribe((pendingItems) => {
        if (pendingItems?.length) {
          this.pendingQueue.push(...pendingItems);
          if (this.connectionOnline) {
            this.retryPendingAttachmentSyncs();
          }
        }
      });
  }

  private persistPendingAttachmentSyncQueue(): void {
    this.dbService
      .clearStore(DBStoreNames.ATTACHMENT_SYNC_QUEUE)
      .pipe(
        switchMap(() =>
          this.pendingQueue.length
            ? this.dbService.bulkAdd(
                DBStoreNames.ATTACHMENT_SYNC_QUEUE,
                this.pendingQueue,
              )
            : of([]),
        ),
        take(1),
      )
      .subscribe(() => {});
  }

  private buildPersistentQueueItem(
    item: AttachmentSyncQueueItem,
  ): AttachmentSyncQueueItem {
    return {
      questionId: item.questionId,
      assessmentProperties: item.assessmentProperties,
      deleteIds: [...item.deleteIds],
      uploads: item.uploads.map((upload) => ({ ...upload })),
    };
  }

  private enqueuePendingQueueItem(item: AttachmentSyncQueueItem): void {
    const persistentItem = this.buildPersistentQueueItem(item);
    const existingIndex = this.pendingQueue.findIndex(
      (queueItem) => queueItem.questionId === persistentItem.questionId,
    );

    if (existingIndex > -1) {
      const existingItem = this.pendingQueue[existingIndex];
      existingItem.deleteIds = Array.from(
        new Set([...existingItem.deleteIds, ...persistentItem.deleteIds]),
      );
      const uploadMap = new Map<string, AttachmentData>();
      [...existingItem.uploads, ...persistentItem.uploads].forEach((upload) => {
        const key = this.getUploadDedupeKey(persistentItem.questionId, upload);
        if (!uploadMap.has(key)) {
          uploadMap.set(key, upload);
        }
      });
      existingItem.uploads = Array.from(uploadMap.values());
      existingItem.assessmentProperties = persistentItem.assessmentProperties;
    } else {
      this.pendingQueue.push(persistentItem);
    }

    this.setAttachmentSyncPending(persistentItem.questionId, true);
    this.persistPendingAttachmentSyncQueue();
  }

  /**
   * submit and sync assessment data to system
   *
   * @param {ISubmitAssessment} assessmentData
   * @return {*}  {Observable<string>}
   * @memberof AssessmentService
   */
  syncQuestionAttachment(
    questionData: ISyncQuestion,
    config: any = { noLoader: true },
  ): Observable<ISyncQuestion> {
    return this.httpClient.post<ISyncQuestion>(
      '/sync-question-attachment',
      questionData,
      {
        params: {
          _config: JSON.stringify(config),
        },
      },
    );
  }

  /**
   * Immediately deletes a single question attachment from blob storage and the DB.
   * The global interceptor loader is bypassed via `skipGlobalLoader`.
   *
   * @param payload  - Request body containing IDs needed by the backend.
   * @param options  - Pass `{ skipGlobalLoader: true }` to suppress the full-page loader.
   */
  deleteQuestionAttachment(
    payload: DeleteAttachmentPayload,
    options: { skipGlobalLoader?: boolean } = {},
  ): Observable<boolean> {
    const config = { noLoader: options.skipGlobalLoader ?? false };
    return this.httpClient.post<boolean>(
      '/delete-question-attachment',
      payload,
      {
        params: {
          _config: JSON.stringify(config),
        },
      },
    );
  }

  /**
   * Returns the Assessment PDF urls
   *
   * @param {number} assessmentID
   * @param {string} sraName
   * @return {*}  {Observable<string[]>}
   * @memberof AssessmentService
   */
  getAssessmentPdfs(
    assessmentID: number,
    sraName: string,
  ): Observable<string[]> {
    return this.httpClient.get<string[]>(`assessments/${assessmentID}/pdfs`, {
      params: { sraName: sraName ?? '' },
    });
  }

  /**
   * Update field validation status
   *
   * @param {number} questionOrder
   * @param {boolean} isValid
   * @memberof AssessmentService
   */
  updateFieldValidation(questionId: number, isValid: boolean): void {
    this.fieldValidations.set(questionId, isValid);
    this.checkFormValidity();
  }

  /**
   * Check if all fields are valid
   *
   * @private
   * @memberof AssessmentService
   */
  checkFormValidity(): void {
    const isValid = Array.from(this.fieldValidations.values()).every(
      (valid) => valid,
    );
    this.formValid.next(isValid);
  }

  /**
   * Get form validity status
   *
   * @return {*}  {boolean}
   * @memberof AssessmentService
   */
  isFormValid(): boolean {
    return this.formValid.value;
  }

  /**
   * Clear validation state
   *
   * @memberof AssessmentService
   */
  clearValidations(): void {
    this.fieldValidations.clear();
    this.formValid.next(true);
  }

  setAttachmentSyncPending(questionId: number, isPending: boolean): void {
    if (isPending) {
      this.pendingAttachmentSyncQuestions.add(questionId);
    } else {
      this.pendingAttachmentSyncQuestions.delete(questionId);
    }
    this.attachmentSyncPending.next(
      this.pendingAttachmentSyncQuestions.size > 0,
    );
  }

  notifyAttachmentSaved(savedAt: Date = new Date()): void {
    this.attachmentSavedAt.next(savedAt);
  }

  /**
   * Mark retry notification as shown to prevent duplicates
   *
   * @memberof AssessmentService
   */
  markRetryNotificationShown(): void {
    this.retryNotificationShown = true;
  }

  /**
   * Reset retry notification flag when going offline
   *
   * @memberof AssessmentService
   */
  resetRetryNotificationFlag(): void {
    this.retryNotificationShown = false;
  }

  /**
   * Check if retry notification has already been shown
   *
   * @return {boolean}
   * @memberof AssessmentService
   */
  isRetryNotificationShown(): boolean {
    return this.retryNotificationShown;
  }

  /**
   * Check if there are pending attachment sync changes for a question
   *
   * @param {number} questionId
   * @return {boolean}
   * @memberof AssessmentService
   */
  isAttachmentSyncPending(questionId: number): boolean {
    return this.pendingAttachmentSyncQuestions.has(questionId);
  }

  /**
   * Initializes sequential attachment sync queue processing.
   *
   * Flow:
   * 1. Listens for queued attachment sync requests.
   * 2. Debounces rapid upload/delete operations.
   * 3. Processes one question sync at a time using concatMap.
   * 4. Tracks syncing state per question.
   * 5. Retries failed syncs when connection is restored.
   *
   * @private
   * @memberof AssessmentService
   */
  private initializeAttachmentSyncQueue(): void {
    this.attachmentSyncQueue$
      .pipe(
        concatMap((item) => {
          this.syncingQuestions.add(item.questionId);
          this.setAttachmentSyncPending(item.questionId, true);

          // Track all upload fileData keys currently in flight
          item.uploads.forEach((u) => {
            if (u.fileData) {
              this.inFlightUploadKeys.add(
                this.getUploadDedupeKey(item.questionId, u),
              );
            }
          });

          return this.processAttachmentSync(item).pipe(
            tap((results) => {
              this.removeSuccessfulOperationsFromPendingQueue(item, results);
              if (item.onSuccess) {
                item.onSuccess(results);
              } else {
                this.attachmentSyncCompleted.next({
                  questionId: item.questionId,
                  results,
                });
              }
            }),
            catchError(() => {
              item.onError?.();
              this.enqueuePendingQueueItem(item);
              return of(null);
            }),
            finalize(() => {
              // Clear in-flight keys for this question when done
              item.uploads.forEach((u) => {
                if (u.fileData) {
                  this.inFlightUploadKeys.delete(
                    this.getUploadDedupeKey(item.questionId, u),
                  );
                }
              });
              this.syncingQuestions.delete(item.questionId);
              this.setAttachmentSyncPending(item.questionId, false);
            }),
          );
        }),
      )
      .subscribe();
  }

  /**
   * Adds attachment sync operation to processing queue.
   *
   * @param {AttachmentSyncQueueItem} item
   * @memberof AssessmentService
   */
queueAttachmentSync(item: AttachmentSyncQueueItem): void {
  // Filter uploads already being processed in the active sync pipeline
  const filteredUploads = item.uploads.filter(
    (u) =>
      !u.fileData ||
      !this.inFlightUploadKeys.has(this.getUploadDedupeKey(item.questionId, u)),
  );
  const filteredItem = { ...item, uploads: filteredUploads };

  // Nothing new to sync after filtering
  if (filteredItem.uploads.length === 0 && filteredItem.deleteIds.length === 0) {
    return;
  }

  if (!this.connectionOnline) {
    this.enqueuePendingQueueItem(filteredItem);
    return;
  }
  if (this.syncingQuestions.has(filteredItem.questionId)) {
    this.enqueuePendingQueueItem(filteredItem);
    return;
  }
  const existingIndex = this.pendingQueue.findIndex(
    (q) => q.questionId === filteredItem.questionId,
  );
  if (existingIndex > -1) {
    this.enqueuePendingQueueItem(filteredItem);
    return;
  }
  this.attachmentSyncQueue$.next(filteredItem);
}

  /**
   * Retries all failed attachment sync operations.
   *
   * Usually triggered when internet connection is restored.
   *
   * @memberof AssessmentService
   */
  retryPendingAttachmentSyncs(): void {
    if (!this.connectionOnline || this.pendingQueue.length === 0) return;

    const itemsToRetry = this.pendingQueue.filter(
      (item) => !this.syncingQuestions.has(item.questionId),
    );
    const itemsStillPending = this.pendingQueue.filter((item) =>
      this.syncingQuestions.has(item.questionId),
    );
    this.pendingQueue.length = 0;
    this.pendingQueue.push(...itemsStillPending);
    this.persistPendingAttachmentSyncQueue();

    for (const item of itemsToRetry) {
      this.attachmentSyncQueue$.next(item);
    }
  }

  private getUploadDedupeKey(
    questionId: number,
    upload: AttachmentData,
  ): string {
    const fileData = upload.fileData ?? '';
    const fileName = upload.fileName ?? '';
    const fileType = upload.fileType ?? '';
    return `${questionId}|${fileData}|${fileName}|${fileType}`;
  }

  private removeSuccessfulOperationsFromPendingQueue(
    item: AttachmentSyncQueueItem,
    results: any[],
  ): void {
    const pendingItem = this.pendingQueue.find(
      (queued) => queued.questionId === item.questionId,
    );
    if (!pendingItem) return;

    const successfulDeletes = new Set<number>();
    const successfulUploadKeys = new Set<string>();

    results
      .filter((result) => result?.success !== false)
      .forEach((result) => {
        if (
          result.type === 'delete' &&
          typeof result.attachmentID === 'number'
        ) {
          successfulDeletes.add(result.attachmentID);
        }
        if (result.type === 'upload' && result.originalAttachment) {
          successfulUploadKeys.add(
            this.getUploadDedupeKey(item.questionId, result.originalAttachment),
          );
        }
      });

    if (successfulDeletes.size === 0 && successfulUploadKeys.size === 0) return;

    pendingItem.deleteIds = pendingItem.deleteIds.filter(
      (deleteId) => !successfulDeletes.has(deleteId),
    );
    pendingItem.uploads = pendingItem.uploads.filter(
      (upload) =>
        !successfulUploadKeys.has(
          this.getUploadDedupeKey(item.questionId, upload),
        ),
    );

    if (
      pendingItem.deleteIds.length === 0 &&
      pendingItem.uploads.length === 0
    ) {
      const itemIndex = this.pendingQueue.findIndex(
        (queued) => queued.questionId === item.questionId,
      );
      if (itemIndex > -1) {
        this.pendingQueue.splice(itemIndex, 1);
      }
      this.setAttachmentSyncPending(item.questionId, false);
    }
    this.persistPendingAttachmentSyncQueue();
  }

  /**
   * Processes attachment uploads and deletions for a question.
   *
   * Operations are executed sequentially:
   * 1. Delete existing attachments marked for removal.
   * 2. Upload new attachments.
   *
   * Each operation is individually protected with catchError
   * so one failure does not terminate the entire queue stream.
   *
   * @private
   * @param {AttachmentSyncQueueItem} item
   * @return {*}  {Observable<any[]>}
   * @memberof AssessmentService
   */
  private processAttachmentSync(
    item: AttachmentSyncQueueItem,
  ): Observable<any[]> {
    const deleteOps = item.deleteIds.map((attachmentId) =>
      this.deleteQuestionAttachment(
        {
          assessmentID: item.assessmentProperties.assessmentID,
          userID: item.assessmentProperties.userID,
          questionID: item.questionId,
          attachmentID: attachmentId,
        },
        { skipGlobalLoader: true },
      ).pipe(
        map(() => ({
          type: 'delete',
          attachmentID: attachmentId,
          success: true,
        })),
        catchError(() =>
          of({ type: 'delete', attachmentID: attachmentId, success: false }),
        ),
      ),
    );

    const uploadOps = item.uploads.map((attachment) => {
      const payload: ISyncQuestion = {
        assessmentID: item.assessmentProperties.assessmentID,
        sraName: item.assessmentProperties.sraName,
        userID: item.assessmentProperties.userID,
        question: {
          questionID: item.questionId,
          attachment: {
            attachmentID: null,
            filePath: null,
            fileData: attachment.fileData,
            fileType: attachment.fileType ?? 'image/png',
            fileName: attachment.fileName ?? null,
            attachmentNotes: attachment.attachmentNotes ?? '',
          },
        },
      };

      return this.syncQuestionAttachment(payload).pipe(
        map((response) => ({
          type: 'upload',
          response,
          originalAttachment: attachment,
          success: true,
        })),
        catchError(() =>
          of({
            type: 'upload',
            originalAttachment: attachment,
            success: false,
          }),
        ),
      );
    });

    return from([...deleteOps, ...uploadOps]).pipe(
      concatMap((operation) => operation),
      toArray(),
      switchMap((results) => {
        const hasFailed = results.some((r) => r?.success === false);
        if (hasFailed) {
          return throwError(
            () => new Error('One or more attachment operations failed'),
          );
        }
        return of(results);
      }),
    );
  }

  /**
   * Checks whether attachment sync is currently in progress
   * for a specific question.
   *
   * @param {number} questionId
   * @return {*}  {boolean}
   * @memberof AssessmentService
   */
  isQuestionSyncing(questionId: number): boolean {
    return this.syncingQuestions.has(questionId);
  }
}
